<template>
    <div class="marketing-container">
        <o-bread-crumb>
            <template v-slot:pageName>Marketing</template>
            <template v-slot:pageDetail>Campaign Management</template>
        </o-bread-crumb>

        <FeatureGate featureKey="marketing_module">
            <div v-if="validation" class="alert alert-danger" role="alert">
                <i class="fas fa-exclamation-circle me-2"></i>{{ validation }}
            </div>

            <div v-if="thisComponentShow" class="marketing-card">
            <!-- Header Section -->
            <div class="marketing-header">
                <div class="header-left">
                    <h4 class="header-title">
                        <i class="fas fa-bullhorn primary-text me-2"></i>Marketing Setup
                    </h4>
                    <p class="header-subtitle">Manage your marketing campaigns and track performance</p>
                </div>
                <div class="header-actions">
                    <button @click="showOnboarding" class="btn-secondary-action">
                        <i class="fas fa-question-circle me-1"></i>
                        <span class="btn-text">About</span>
                    </button>
                    <button @click="toggleAnalytics" class="btn-secondary-action">
                        <i class="fas fa-chart-line me-1"></i>
                        <span class="btn-text">Analytics</span>
                    </button>
                    <button 
                        @click="handleCreateClick" 
                        :disabled="!write"
                        :class="['btn-primary-action', { 'btn-disabled': !write }]"
                        :title="!write ? 'You only have read access to this module' : 'Create new marketing'">
                        <i class="fas fa-plus me-1"></i>
                        <span class="btn-text">Create Marketing</span>
                    </button>
                </div>
            </div>

            <!-- Content Section -->
            <div class="marketing-content">
                <div v-show="page.isLoading" class="loading-state">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="loading-text">Loading marketings...</p>
                </div>
                
                <div v-show="!page.isLoading && page.output">
                    <!-- Filter & Action Bar -->
                    <div class="filter-action-wrapper mb-4 mt-3">
                        <div class="filter-controls">
                            <!-- Search by Name -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Search</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="search-input-wrapper">
                                        <i class="fas fa-search search-icon-filter"></i>
                                        <input
                                            v-model="searchName"
                                            type="text"
                                            placeholder="Search by name..."
                                            class="form-control-filter"
                                            @keyup.enter="applyFilter"
                                        />
                                        <button v-if="searchName" class="btn-clear-input" @click="searchName = ''" title="Clear">
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Time Scale Filter -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Period</span>
                                </div>
                                <div class="date-range-box">
                                    <select class="form-select form-control-filter" v-model="selectedTimeScale" @change="handleTimeScaleChange">
                                        <option value="">All Time</option>
                                        <option value="day">Today</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="custom">Custom Range</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Date Range Picker (only for custom) -->
                            <div class="filter-item" v-show="selectedTimeScale === 'custom'">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Date Range</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="date-input-wrapper">
                                        <input
                                            placeholder="Select Date Range"
                                            ref="datePicker"
                                            class="form-control-filter"
                                        />
                                        <i class="fas fa-calendar-alt calendar-icon-filter"></i>
                                    </div>
                                </div>
                            </div>

                            <!-- Sort By -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Sort By</span>
                                </div>
                                <div class="date-range-box">
                                    <select class="form-select form-control-filter" v-model="selectedSortBy">
                                        <option value="created_at">Created Date</option>
                                        <option value="name">Name</option>
                                        <option value="total_events">Total Events</option>
                                        <option value="total_members">Total Members</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Sort Direction -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Sort</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="sort-toggle-box">
                                        <span class="sort-label" :class="{ active: !isDesc }">ASC</span>
                                        <label class="switch-sm">
                                            <input type="checkbox" v-model="isDesc">
                                            <span class="slider-sm round"></span>
                                        </label>
                                        <span class="sort-label" :class="{ active: isDesc }">DESC</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button @click="applyFilter" class="btn btn-apply-filter">
                                    <i class="fas fa-filter me-1"></i> Apply
                                </button>
                                <button v-if="isFilterApplied" @click="clearFilter" class="btn btn-clear-filter" title="Clear Filter">
                                    <i class="fas fa-times me-1"></i> Clear
                                </button>
                                <button class="btn-refresh" @click.prevent="syncData()" :disabled="isSyncing" title="Sync Data & Refresh">
                                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': isSyncing }"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Table Section -->
                    <div class="table-wrapper">
                        <div v-if="marketings.length === 0" class="empty-state">
                            <i class="fas fa-inbox fa-3x"></i>
                            <h5>No Marketing Setup Yet</h5>
                            <p>Create your first marketing setup to start tracking campaigns</p>
                        </div>

                        <o-table v-else class="marketing-table">
                            <thead>
                                <tr>
                                    <td class="fw-bolder text-muted" scope="col">No</td>
                                    <td class="fw-bolder text-muted" scope="col">Name</td>
                                    <td class="fw-bolder text-muted" scope="col">Assigned To</td>
                                    <td class="fw-bolder text-muted" scope="col">Campaigns</td>
                                    <td class="fw-bolder text-muted text-center" scope="col">Total Events</td>
                                    <td class="fw-bolder text-muted text-center" scope="col">Total Members</td>
                                    <td class="fw-bolder text-muted" scope="col">Created At</td>
                                    <td class="fw-bolder text-muted text-center" scope="col">Action</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in marketings" :key="item.id" @click="goToDetail(item.id)" class="marketing-row-clickable">
                                    <td><span class="date-text">{{ (meta.page - 1) * meta.limit + index + 1 }}</span></td>
                                    <td>
                                        <div class="name-wrapper">
                                            <i class="fas fa-bullhorn primary-text me-2"></i>
                                            <span class="name-text">{{ item.name }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <i class="fas fa-user-circle merchant-text me-2"></i>
                                        <span class="merchant-text">{{ item.assigned_user?.first_name }} {{ item.assigned_user?.last_name }}</span>
                                    </td>
                                    <td>
                                        <span class="badge-count">{{ item.campaigns?.length || 0 }}</span>
                                    </td>
                                    <td class="text-center">
                                        <div v-if="isSyncing" class="spinner-border spinner-border-sm text-primary mx-auto d-block" role="status"></div>
                                        <span v-else class="amount-positive">{{ item.total_events ?? 0 }}</span>
                                    </td>
                                    <td class="text-center">
                                        <div v-if="isSyncing" class="spinner-border spinner-border-sm text-primary mx-auto d-block" role="status"></div>
                                        <span v-else class="member-count">{{ item.total_members ?? 0 }}</span>
                                    </td>
                                    <td><span class="date-text">{{ formatDate(item.created_at) }}</span></td>
                                    <td class="text-center" @click.stop>
                                        <div class="action-buttons">
                                            <button class="btn btn-action btn-detail me-2" @click="goToDetail(item.id)" title="View Details">
                                                <i class="fas fa-eye"></i>
                                            </button>
                                            <button class="btn btn-action btn-edit me-2" v-if="write" @click="editModal(item)" title="Edit">
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button class="btn btn-action btn-delete" v-if="write" @click="confirmDelete(item)" title="Delete">
                                                <i class="fas fa-trash"></i>
                                            </button>
                                            <button 
                                                v-if="!write" 
                                                class="btn btn-action btn-locked" 
                                                disabled 
                                                title="You only have read access">
                                                <i class="fas fa-lock"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </o-table>
                    </div>

                    <!-- Pagination -->
                    <div class="pagination-wrapper">
                        <o-pagination :page="meta.page" :total="meta.total" @callback="navPage"/>
                    </div>
                </div>
            </div>
        </div>

        <!-- Marketing Analytics Section -->
        <div ref="analyticsSection">
            <marketing-analytics v-if="isAnalyticsVisible" @close="toggleAnalytics" class="mt-4" />
        </div>

        <!-- Marketing Form Modal -->
        <marketing-form
            v-if="isModalVisible"
            :editedItem="editedItem"
            @save="saveMarketing"
            @close="closeModal"
        ></marketing-form>

        <!-- Onboarding Modal -->
        <o-modal v-if="isOnboardingVisible" @close="closeOnboarding" modal-class="show d-block">
            <template v-slot:header>
                <h5 class="modal-title">
                    <i class="fas fa-info-circle primary-text me-2"></i>About Marketing Feature
                </h5>
            </template>
            <template v-slot:body>
                <div class="onboarding-content">
                    <div class="feature-section">
                        <div class="feature-icon">
                            <i class="fas fa-bullhorn"></i>
                        </div>
                        <h6>Marketing Module</h6>
                        <p>Advanced feature to track member activities through campaign tracking system.</p>
                    </div>
                    
                    <div class="features-list">
                        <h6>Key Features:</h6>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span>Create and manage marketing setups</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span>Assign marketing to specific users</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span>Create multiple campaigns per marketing</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span>Track member activities per campaign</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span>Auto-generate referral codes</span>
                        </div>
                    </div>
                </div>
            </template>
        </o-modal>

        <!-- Delete Confirmation -->
        <o-warning-box
            v-if="isDeleteConfirmVisible"
            title="Delete Marketing"
            :item-name="itemToDelete?.name || 'this marketing'"
            warning-message="This will also delete all associated campaigns."
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            @confirm="deleteMarketing"
            @cancel="closeDeleteConfirm"
        />
        </FeatureGate>
    </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, nextTick, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { OTable, OPagination, OBreadCrumb, OWarningBox, OModal } from '@/components/OmnihControl'
import FeatureGate from '@/components/upsell/FeatureGate.vue'
import MarketingForm from './MarketingForm.vue'
import MarketingAnalytics from './MarketingAnalytics.vue'
import { useToast } from '@/composables/useToast'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'

export default {
    name: 'Marketing',
    components: {
        OTable,
        OPagination,
        OBreadCrumb,
        OWarningBox,
        OModal,
        FeatureGate,
        MarketingForm,
        MarketingAnalytics,
    },
    setup() {
        const store = useStore()
        const router = useRouter()

        const page = reactive({
            isLoading: false,
            output: false,
            component: 'marketing'
        })

        const validation = ref('')
        const thisComponentShow = ref(false)
        const write = ref(false)
        
        const isModalVisible = ref(false)
        const editedItem = ref(null)
        const isOnboardingVisible = ref(false)
        const isDeleteConfirmVisible = ref(false)
        const itemToDelete = ref(null)
        const isSyncing = ref(false)
        const isAnalyticsVisible = ref(false)
        const analyticsSection = ref(null)

        const toggleAnalytics = async () => {
            isAnalyticsVisible.value = !isAnalyticsVisible.value
            if (isAnalyticsVisible.value) {
                await nextTick()
                if (analyticsSection.value) {
                    analyticsSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }
        }

        // Filter state
        const searchName = ref('')
        const selectedTimeScale = ref('')
        const selectedSortBy = ref('created_at')
        const isDesc = ref(true)
        const isFilterApplied = ref(false)
        const dateRange = ref([null, null])
        const datePicker = ref(null)
        let flatpickrInstance = null

        const { showToast } = useToast()

        const marketings = computed(() => store.state.Marketing?.marketings || [])
        const meta = computed(() => store.state.Marketing?.meta || {})
        
        // Check if marketing module is enabled via feature flag from database
        const isMarketingModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.marketingModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        // Re-initialize flatpickr when custom date range is selected
        watch(selectedTimeScale, async (val) => {
            if (val === 'custom') {
                await nextTick()
                if (datePicker.value && !flatpickrInstance) {
                    flatpickrInstance = flatpickr(datePicker.value, {
                        mode: 'range',
                        maxRange: 31,
                        onChange: (selectedDates) => {
                            if (selectedDates.length === 2) {
                                dateRange.value = selectedDates
                            }
                        },
                    })
                }
            } else {
                // Destroy flatpickr when not custom
                if (flatpickrInstance) {
                    flatpickrInstance.destroy()
                    flatpickrInstance = null
                }
                dateRange.value = [null, null]
            }
        })

        onBeforeUnmount(() => {
            if (flatpickrInstance) {
                flatpickrInstance.destroy()
                flatpickrInstance = null
            }
        })

        onMounted(() => {
            loadState()
        })

        function handleTimeScaleChange() {
            // Reset date range when switching period modes
            dateRange.value = [null, null]
        }

        function applyFilter() {
            if (searchName.value) {
                store.commit('Marketing/set_search', { search: { name: searchName.value } })
            } else {
                store.commit('Marketing/clear_search')
            }

            const filterData = {
                time_scale: selectedTimeScale.value || null,
                date_from: null,
                date_to: null,
            }

            if (selectedTimeScale.value === 'custom' && dateRange.value[0] && dateRange.value[1]) {
                filterData.date_from = formatDateToYYYYMMDD(dateRange.value[0])
                filterData.date_to = formatDateToYYYYMMDD(dateRange.value[1])
            }

            store.commit('Marketing/set_time_filter', filterData)

            const updatedMeta = {
                ...meta.value,
                page: 1,
                sortBy: selectedSortBy.value,
                sortAsc: !isDesc.value,
            }
            store.commit('Marketing/switch_page', { meta: updatedMeta })

            isFilterApplied.value = true
            page.isLoading = true

            store.dispatch('Marketing/read')
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load marketings'
                })
                .finally(() => {
                    page.isLoading = false
                })
        }

        function clearFilter() {
            searchName.value = ''
            selectedTimeScale.value = ''
            selectedSortBy.value = 'created_at'
            isDesc.value = true
            dateRange.value = [null, null]
            isFilterApplied.value = false

            if (flatpickrInstance) {
                flatpickrInstance.clear()
                if (flatpickrInstance.input) {
                    flatpickrInstance.input.value = ''
                }
            }

            store.commit('Marketing/clear_search')
            store.commit('Marketing/clear_time_filter')

            const updatedMeta = { ...meta.value, page: 1, sortBy: 'created_at', sortAsc: false }
            store.commit('Marketing/switch_page', { meta: updatedMeta })

            page.isLoading = true
            store.dispatch('Marketing/read')
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load marketings'
                })
                .finally(() => {
                    page.isLoading = false
                })
        }

        function formatDateToYYYYMMDD(date) {
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        function loadState() {
            page.isLoading = true
            validation.value = ''

            store.dispatch('Marketing/read')
                .then(() => {
                    if (store.state.Auth.currUser.level === 'root') {
                        write.value = true
                        thisComponentShow.value = true
                    } else {
                        const userModules = store.state.Auth.userInfo.modules
                        if (userModules) {
                            const matchingModule = userModules.find(module => module.module_name === page.component)
                            write.value = matchingModule ? matchingModule.write : false
                            thisComponentShow.value = matchingModule ? true : false
                        }
                    }
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load marketings'
                })
                .finally(() => {
                    page.isLoading = false
                    page.output = true
                })
        }

        function handleCreateClick() {
            if (!write.value) {
                validation.value = 'You only have read access to this module. Write permission is required to create marketing.'
                setTimeout(() => {
                    validation.value = ''
                }, 5000)
                return
            }
            showCreateModal()
        }

        function showCreateModal() {
            editedItem.value = null
            isModalVisible.value = true
        }

        function editModal(item) {
            editedItem.value = item
            isModalVisible.value = true
        }

        function closeModal() {
            isModalVisible.value = false
            editedItem.value = null
        }

        function saveMarketing(data) {
            const action = data.id ? 'Marketing/update' : 'Marketing/create'
            const isUpdate = !!data.id

            return store.dispatch(action, { data })
                .then((response) => {
                    loadState()
                    closeModal()

                    const createdName = response?.data?.data?.name || data.name || '-'

                    showToast({
                        type: 'success',
                        title: isUpdate ? 'Update Successful' : 'Creation Successful',
                        message: isUpdate 
                            ? `Marketing "${data.name}" has been successfully updated.`
                            : `Marketing "${createdName}" has been successfully created.`,
                    })
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to save marketing'
                    showToast({
                        type: 'error',
                        title: 'Save Failed',
                        message: validation.value,
                    })
                    throw err // Re-throw to let the form handle it
                })
        }

        function confirmDelete(item) {
            itemToDelete.value = item
            isDeleteConfirmVisible.value = true
        }

        function closeDeleteConfirm() {
            isDeleteConfirmVisible.value = false
            itemToDelete.value = null
        }

        function deleteMarketing() {
            if (!itemToDelete.value) return

            const deletedName = itemToDelete.value.name
            store.dispatch('Marketing/delete', itemToDelete.value.id)
                .then(() => {
                    loadState()
                    closeDeleteConfirm()
                    showToast({
                        type: 'success',
                        title: 'Deletion Successful',
                        message: `Marketing "${deletedName}" has been successfully deleted.`,
                    })
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to delete marketing'
                    closeDeleteConfirm()
                    showToast({
                        type: 'error',
                        title: 'Delete Failed',
                        message: validation.value,
                    })
                })
        }

        function goToDetail(id) {
            router.push({ name: 'MarketingDetail', params: { id } })
        }

        function showOnboarding() {
            isOnboardingVisible.value = true
        }

        function closeOnboarding() {
            isOnboardingVisible.value = false
        }

        function navPage(pageNum) {
            const updatedMeta = { ...meta.value, page: pageNum }
            store.commit('Marketing/switch_page', { meta: updatedMeta })
            store.dispatch('Marketing/read')
        }

        function syncData() {
            if (isSyncing.value) return
            
            isSyncing.value = true
            store.dispatch('Marketing/sync')
                .then(() => {
                    showToast({
                        type: 'success',
                        title: 'Sync Successful',
                        message: 'Marketing data has been synchronized with the latest events.',
                    })
                    loadState()
                })
                .catch((err) => {
                    showToast({
                        type: 'error',
                        title: 'Sync Failed',
                        message: err.response?.data?.message || 'Failed to sync marketing data.',
                    })
                })
                .finally(() => {
                    isSyncing.value = false
                })
        }

        function formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('en-GB')
        }

        return {
            page,
            validation,
            thisComponentShow,
            write,
            isModalVisible,
            editedItem,
            isOnboardingVisible,
            isDeleteConfirmVisible,
            itemToDelete,
            marketings,
            meta,
            isMarketingModuleEnabled,
            searchName,
            selectedTimeScale,
            selectedSortBy,
            isDesc,
            isFilterApplied,
            datePicker,
            isSyncing,
            isAnalyticsVisible,
            analyticsSection,
            toggleAnalytics,
            loadState,
            syncData,
            handleCreateClick,
            showCreateModal,
            editModal,
            closeModal,
            saveMarketing,
            confirmDelete,
            closeDeleteConfirm,
            deleteMarketing,
            goToDetail,
            showOnboarding,
            closeOnboarding,
            navPage,
            formatDate,
            applyFilter,
            clearFilter,
            handleTimeScaleChange,
        }
    }
}
</script>

<style scoped>
/* Feature Disabled Container */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Container */
.marketing-container {
    padding: 16px;
    padding-right: 20px;
    max-width: 100%;
}

/* Main Card */
.marketing-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
}

/* Header */
.marketing-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
    gap: 1rem;
}

.header-left {
    flex: 1;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
}

/* Buttons */
.btn-primary-action {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-primary-action:hover {
    background: var(--primary-color-dark, #2563eb);
    transform: translateY(-1px);
}

.btn-primary-action.btn-disabled,
.btn-primary-action:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
}

.btn-primary-action.btn-disabled:hover,
.btn-primary-action:disabled:hover {
    background: #9ca3af;
    transform: none;
}

.btn-secondary-action {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-secondary-action:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Content */
.marketing-content {
    padding: 1.5rem 2rem;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
}

.loading-text {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

/* Filter Bar */
.filter-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    gap: 1rem;
}

.search-wrapper {
    flex: 1;
    max-width: 400px;
}

/* Filter & Action Bar (TransactionReport pattern) */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 140px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px;
    min-width: 160px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
    outline: none;
}

/* Search Input Wrapper */
.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-input-wrapper .form-control-filter {
    padding-left: 36px;
    padding-right: 36px;
    min-width: 200px;
}

.search-icon-filter {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    pointer-events: none;
    z-index: 1;
}

.btn-clear-input {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    font-size: 12px;
}

.btn-clear-input:hover {
    color: #64748b;
}

/* Date Input Wrapper */
.date-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.date-input-wrapper .form-control-filter {
    padding-right: 36px;
    width: 220px;
}

.calendar-icon-filter {
    position: absolute;
    right: 12px;
    color: var(--primary-color, #FC6666);
    pointer-events: none;
}

/* Sort Toggle Box */
.sort-toggle-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
}

.sort-label {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    transition: color 0.2s;
}

.sort-label.active {
    color: var(--primary-color, #FC6666);
}

/* Small Switch */
.switch-sm {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
}

.switch-sm input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider-sm {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .3s;
    border-radius: 22px;
}

.slider-sm:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
}

input:checked + .slider-sm {
    background-color: var(--primary-color, #FC6666);
}

input:checked + .slider-sm:before {
    transform: translateX(18px);
}

/* Action Section */
.action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-apply-filter {
    background-color: var(--primary-color, #FC6666);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
}

.btn-apply-filter:hover {
    filter: brightness(0.9);
    color: white;
}

.btn-clear-filter {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    transition: all 0.2s;
}

.btn-clear-filter:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

/* Refresh button */
.btn-refresh {
    background: white;
    color: var(--primary-color, #3b82f6);
    border: 1px solid #e5e7eb;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-refresh:hover {
    background: #f9fafb;
    border-color: var(--primary-color, #3b82f6);
}

/* Marketing Table (TransactionReport style) */
.marketing-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    width: 100%;
}

.marketing-table th,
.marketing-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.marketing-row-clickable {
    cursor: pointer;
    transition: background-color 0.2s;
}

.marketing-row-clickable:hover {
    background-color: #fff7f7 !important;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

.name-text {
    font-weight: 600;
    font-size: 13px;
    color: #374151;
}

.merchant-text {
    font-size: 13px;
    color: #6b7280;
}

.amount-positive {
    font-weight: 700;
    color: #3CAA94;
    display: block;
    text-align: center !important;
}

.member-count {
    font-weight: 700;
    color: #6366f1;
    display: block;
    text-align: center !important;
}

/* Flatpickr Override */
.flatpickr-input {
    width: 100%;
    font-weight: 600;
    font-size: 14px;
    border: 1px solid #e2e8f0;
    height: 46px;
    border-radius: 8px;
    padding: 0.375rem 0.75rem;
    background-clip: padding-box;
    background-color: #fff !important;
}

/* Table */
.table-wrapper {
    overflow-x: auto;
    margin-bottom: 1.5rem;
}

.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-table thead th {
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
}

.modern-table tbody tr {
    border-bottom: 1px solid #f3f4f6;
    transition: background-color 0.15s ease;
}

.table-row-clickable {
    cursor: pointer;
}

.modern-table tbody tr:hover {
    background-color: #f9fafb;
}

.modern-table tbody td {
    padding: 1rem;
    font-size: 0.875rem;
    color: #374151;
}

/* Table Columns */
.col-number {
    width: 60px;
    color: #9ca3af;
    font-weight: 500;
}

.col-name .name-wrapper,
.col-user .user-wrapper {
    display: flex;
    align-items: center;
}

.col-name .fw-semibold {
    color: #111827;
}

.badge-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 24px;
    padding: 0 8px;
    background: var(--primary-color, #3b82f6);
    color: white;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    
}

.col-date {
    color: #6b7280;
}

/* Table Action Buttons */
.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
}

.btn-detail {
    background-color: #f0f9ff;
    color: #0369a1;
    width: 32px;
    height: 32px;
    padding: 0;
    justify-content: center;
}

.btn-detail:hover {
    background-color: #e0f2fe;
    color: #075985;
}

.btn-edit {
    background-color: #fef3c7;
    color: #b45309;
    width: 32px;
    height: 32px;
    padding: 0;
    justify-content: center;
}

.btn-edit:hover {
    background-color: #fde68a;
    color: #92400e;
}

.btn-delete {
    background-color: #fee2e2;
    color: #dc2626;
    width: 32px;
    height: 32px;
    padding: 0;
    justify-content: center;
}

.btn-delete:hover {
    background-color: #fecaca;
    color: #b91c1c;
}

.btn-locked {
    background-color: #f3f4f6;
    color: #9ca3af;
    width: 32px;
    height: 32px;
    padding: 0;
    justify-content: center;
    cursor: not-allowed;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: #9ca3af;
}

.empty-state i {
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state h5 {
    color: #374151;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: #6b7280;
    font-size: 0.875rem;
}

/* Pagination */
.pagination-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 1rem;
}

/* Alert */
.alert {
    border-radius: 8px;
    border: none;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
}

/* Modal Content */
.onboarding-content {
    padding: 1rem;
}

.feature-section {
    text-align: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.feature-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    background: linear-gradient(135deg, var(--primary-color, #3b82f6) 0%, #2563eb 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 2rem;
}

.feature-section h6 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
}

.feature-section p {
    color: #6b7280;
    margin: 0;
}

.features-list h6 {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1rem;
}

.feature-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 0;
}

.feature-item i {
    margin-top: 2px;
    font-size: 1rem;
}

.feature-item span {
    color: #374151;
    font-size: 0.9375rem;
}

.primary-text {
    color: var(--primary-color, #F93E3E) !important;
}

/* Responsive */
@media (max-width: 768px) {
    .marketing-header {
        flex-direction: column;
        align-items: flex-start;
        padding: 1.25rem;
    }

    .header-actions {
        width: 100%;
        flex-direction: column;
    }

    .btn-primary-action,
    .btn-secondary-action {
        width: 100%;
        justify-content: center;
    }

    .btn-text {
        display: inline;
    }

    .marketing-content {
        padding: 1rem;
    }

    .filter-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .search-wrapper {
        max-width: 100%;
    }

    .btn-refresh {
        width: 100%;
        height: 40px;
    }

    .modern-table {
        font-size: 0.8125rem;
    }

    .modern-table tbody td {
        padding: 0.75rem 0.5rem;
    }

    .action-buttons {
        flex-direction: column;
        gap: 0.25rem;
    }

    .btn-action {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .header-title {
        font-size: 1.25rem;
    }

    .header-subtitle {
        font-size: 0.8125rem;
    }

    .modern-table thead th {
        padding: 0.5rem;
        font-size: 0.6875rem;
    }
}
</style>
